import type {
  ReceivedReferralAttachment,
  Referral,
  ReferralLetterReceiver,
  ReferralLetterValues,
} from '@/hooks/useReferrals';
import {
  medcomIdentifierDisplay,
  referralSenderIdentity,
  senderIdentifierHeadingKey,
  trimIdentifierPart,
} from '@/lib/referral/senderIdentifier';
import { referDescriptionDisplay } from '@/lib/referral/xref01Labels';
import { formatDanishDateTime } from '../danishDateTime';

// C7 (REF01-M / REF06-M udskrift): printable view of a received referral.
// Patient name and CPR repeat at the top of every printed page (a <thead> the
// browser repeats per page) and "Side n af m" sits at the bottom of every page
// (@page margin box). The CPR is passed in only after an audited reveal; it is
// never logged or stored by this module.

export type PrintTranslate = (key: string, options?: Record<string, unknown>) => string;

type Row = [label: string, value: string | null | undefined];
type Section = { title: string; rows?: Row[]; blocks?: Row[] };

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** CSS string literal for @page content: backslash, quote and newline escaped. */
export function cssString(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ')}"`;
}

function txt(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function rec(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function recs(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(rec) : [];
}

function join(parts: (string | null | undefined)[], sep: string): string | null {
  const kept = parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  return kept.length > 0 ? kept.join(sep) : null;
}

function addressText(a: Record<string, unknown>): string | null {
  return join(
    [txt(a.streetName), txt(a.suburbName), join([txt(a.postCodeIdentifier), txt(a.districtName)], ' ')],
    ', ',
  );
}

function diagnosis(code: string | null, label: string | null): string | null {
  return join([code, label], ' — ');
}

function dateTime(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return formatDanishDateTime(d);
}

export function formatCprForPrint(cpr: string): string {
  return /^\d{10}$/.test(cpr) ? `${cpr.slice(0, 6)}-${cpr.slice(6)}` : cpr;
}

function bytesText(size: number | null | undefined): string | null {
  if (typeof size !== 'number' || !Number.isFinite(size)) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function normalizeObjectId(value: string | null | undefined): string {
  return (value ?? '').replace(/[^0-9a-zA-Z]/g, '').toLowerCase();
}

/** C7: Receiver from the stored letter (XREF06) or from medcom_details (XREF01). */
function receiverRows(r: Referral, letter: ReferralLetterReceiver | null | undefined, t: PrintTranslate): Row[] {
  const d = rec(rec(r.medcom_details).receiver);
  const ean = letter?.ean ?? txt(d.ean);
  const identifierCode = letter?.identifier_code ?? txt(d.identifierCode);
  const identifier = trimIdentifierPart(letter?.identifier ?? txt(d.identifier));
  const address = letter
    ? join([letter.street_name, letter.suburb_name, join([letter.post_code, letter.district_name], ' ')], ', ')
    : addressText(d);
  const idDisplay = medcomIdentifierDisplay(identifier, identifierCode);
  return [
    [t('referrals.xref01_ean'), ean],
    idDisplay
      ? ([t(idDisplay.headingKey), idDisplay.value] as Row)
      : ([t(senderIdentifierHeadingKey(null)), identifier] as Row),
    [t('referrals.xref01_organisation'), letter?.organisation_name ?? txt(d.organisationName)],
    [t('referrals.xref01_department'), letter?.department_name ?? txt(d.departmentName)],
    [t('referrals.xref01_unit'), letter?.unit_name ?? txt(d.unitName)],
    [t('referrals.xref01_address'), address],
  ];
}

function referralSections(r: Referral, t: PrintTranslate, extra: PrintExtras = {}): Section[] {
  const d = rec(r.medcom_details);
  const phones = r.patient_phones
    .map((p) => join([p.telephoneSubscriberIdentifier, p.telephoneSubscriberCode ? `(${p.telephoneSubscriberCode})` : null], ' '))
    .filter((p): p is string => p !== null);
  const sections: Section[] = [];

  sections.push({
    title: t('referrals.section_patient'),
    rows: [
      [t('referrals.field_full_name'), join([r.first_name, r.last_name], ' ')],
      [t('referrals.medcom_field_address'), addressText(rec(r.patient_address))],
      [t('referrals.field_phone'), join(phones.length > 0 ? phones : [r.phone], ', ')],
      [t('referrals.field_email'), r.email],
      [t('referrals.medcom_field_job_title'), r.patient_job_title],
    ],
  });

  // REF06-M 3.5/3.6/3.14: the letter's own values when they could be read.
  const letter = extra.letter ?? null;
  const relativeName = letter?.relative
    ? join([letter.relative.given_name, letter.relative.surname], ' ')
    : r.parent_1_name;
  const relationCode = letter?.relative ? letter.relative.relation_code : r.relative_relation_code;
  if (relativeName || relationCode) {
    sections.push({
      title: t('referrals.medcom_section_relative'),
      rows: [
        [t('referrals.medcom_field_relative_name'), relativeName],
        [t('referrals.medcom_field_relation'), relationCode],
      ],
    });
  }

  const referrer = rec(d.referrer);
  const stored = referralSenderIdentity(r);
  const fileSender = letter?.sender ?? null;
  const senderIdentity = {
    identifier: stored.identifier ?? fileSender?.identifier ?? null,
    identifierCode: stored.identifierCode ?? fileSender?.identifier_code ?? null,
  };
  const fileReferrer = letter?.referrer ?? null;
  const refer = letter?.refer && (letter.refer.code || letter.refer.text) ? letter.refer : null;
  const episodes = recs(d.episodesOfCare);
  const lpr = episodes.find((e) => (txt(e.type) ?? '').toUpperCase().startsWith('LPR'));
  sections.push({
    title: t('referrals.section_letter'),
    rows: [
      [t('referrals.xref01_standard'), r.medcom_type_code],
      [t('referrals.xref01_referral_date'), txt(d.referralDate) ?? dateTime(r.sent_at)],
      [t('referrals.print_received_at'), dateTime(r.received_at)],
      [t('referrals.medcom_field_envelope_id'), extra.envelopeIdentifier ?? null],
      [t('referrals.medcom_field_letter_id'), r.medcom_letter_identifier],
      [t('referrals.medcom_field_episode_of_care_id'), r.episode_of_care_identifier],
      [t('referrals.xref01_lpr3_uuid'), lpr ? txt(lpr.identifier) : null],
      [t('referrals.xref01_priority'), txt(d.priority)],
      [t('referrals.xref01_referral_type'), txt(d.referralStatus)],
      [t('referrals.xref01_transport'), txt(d.transport)],
      [t('referrals.xref01_hospital_choice'), txt(d.hospitalChoice)],
      [
        t('referrals.field_referral_diagnosis'),
        refer ? diagnosis(refer.code, refer.text) : diagnosis(r.referral_diagnosis_code, r.referral_diagnosis),
      ],
      [t('referrals.xref01_diagnosis_kind'), referDescriptionDisplay(txt(d.referDescriptionCode))],
      ...recs(d.additionalDiagnoses).map((a): Row => [
        t('referrals.xref01_additional_diagnosis'),
        join([diagnosis(txt(a.diagnoseCode), txt(a.diagnoseText)), txt(a.diagnoseDescriptionCode) ? `(${txt(a.diagnoseDescriptionCode)})` : null], ' '),
      ]),
    ],
    blocks: [
      [t('referrals.field_requested_examination'), r.requested_examination ?? txt(d.clinicalReason)],
      [t('referrals.xref01_supplementary'), txt(d.supplementary)],
    ],
  });

  sections.push({
    title: t('referrals.medcom_section_clinical'),
    blocks: [
      [t('referrals.medcom_field_cave'), r.cave],
      [t('referrals.field_anamnese'), r.anamnese],
      [t('referrals.medcom_field_examination'), r.examination],
      [t('referrals.medcom_field_medicine'), r.actual_medicine],
    ],
  });

  const senderIdDisplay = medcomIdentifierDisplay(senderIdentity.identifier, senderIdentity.identifierCode);
  const referrerIdDisplay = fileReferrer?.identifier
    ? medcomIdentifierDisplay(fileReferrer.identifier, fileReferrer.identifier_code, {
        fallbackIdentifierCode: senderIdentity.identifierCode,
      })
    : null;

  sections.push({
    title: t('referrals.section_sender'),
    rows: [
      [t('referrals.medcom_field_sender_ean'), r.sender_ean],
      [t('referrals.field_sender_hospital'), r.sender_hospital ?? r.referrer_name],
      [t('referrals.field_sender_department'), fileSender?.department_name ?? r.sender_department],
      [t('referrals.xref01_unit'), fileSender?.unit_name],
      [
        t('referrals.xref01_address'),
        fileSender
          ? join([fileSender.street_name, fileSender.suburb_name, join([fileSender.post_code, fileSender.district_name], ' ')], ', ')
          : null,
      ],
      [t('referrals.field_phone'), fileSender?.telephone],
      [t('referrals.field_referring_doctor_name'), r.referring_doctor_name],
      [t('referrals.xref01_referrer_title'), txt(referrer.personTitle)],
      ...(senderIdDisplay ? ([[t(senderIdDisplay.headingKey), senderIdDisplay.value] as Row] as Row[]) : []),
      ...(referrerIdDisplay ? ([[t(referrerIdDisplay.headingKey), referrerIdDisplay.value] as Row] as Row[]) : []),
    ],
  });

  if (r.source === 'medcom') {
    const rows = receiverRows(r, extra.receiver, t);
    const any = rows.some(([, v]) => v);
    sections.push({
      title: t('referrals.xref01_section_receiver'),
      rows: any
        ? rows
        : [[t('referrals.xref01_section_receiver'), extra.receiverFailed ? t('referrals.print_receiver_unavailable') : t('referrals.print_not_stated')]],
    });
  }

  const cc = rec(r.answer_cc_receiver);
  if (Object.keys(cc).length > 0) {
    const ccIdDisplay = medcomIdentifierDisplay(txt(cc.identifier), txt(cc.identifierCode));
    sections.push({
      title: t('referrals.medcom_section_cc'),
      rows: [
        ...(ccIdDisplay ? ([[t(ccIdDisplay.headingKey), ccIdDisplay.value] as Row] as Row[]) : []),
        [t('referrals.xref01_organisation'), txt(cc.organisationName)],
        [t('referrals.xref01_department'), txt(cc.departmentName)],
        [t('referrals.xref01_unit'), txt(cc.unitName)],
        [t('referrals.xref01_address'), addressText(cc)],
      ],
    });
  }

  const visits = recs(d.hospitalVisitations);
  if (visits.length > 0) {
    sections.push({
      title: t('referrals.xref01_section_hospital_visits'),
      blocks: visits.map((v): Row => [
        txt(v.informationCode) ?? t('referrals.xref01_hospital_visit'),
        Array.isArray(v.information)
          ? join(v.information.filter((l): l is string => typeof l === 'string'), '\n')
          : null,
      ]),
    });
  }

  // C7: Bilag always printed for a received referral, with each attachment's
  // name (RefDescription and the received file name), type, size and status.
  if (r.source === 'medcom' || r.attachments.length > 0) {
    const received = extra.receivedAttachments ?? [];
    sections.push({
      title: t('referrals.medcom_section_attachments'),
      rows: r.attachments.length === 0
        ? [[t('referrals.medcom_section_attachments'), t('referrals.print_attachments_none')]]
        : r.attachments.map((a, index): Row => {
          const got = received.find((x) => normalizeObjectId(x.object_identifier) === normalizeObjectId(a.objectIdentifier));
          const status = got?.received
            ? t('referrals.print_attachment_received')
            : extra.receivedAttachments
              ? t('referrals.print_attachment_waiting')
              : null;
          return [
            a.refDescription ?? got?.file_name ?? `${t('referrals.medcom_section_attachments')} ${index + 1}`,
            join([
              got?.file_name ?? null,
              join([a.objectCode, a.objectExtensionCode], ' '),
              a.originalObjectSize ? bytesText(Number(a.originalObjectSize)) ?? `${a.originalObjectSize} B` : null,
              status,
              a.url,
              a.objectIdentifier,
            ], ' · '),
          ];
        }),
    });
  }

  return sections;
}

function renderSection(section: Section): string {
  const rows = (section.rows ?? []).filter(([, v]) => v);
  const blocks = (section.blocks ?? []).filter(([, v]) => v);
  if (rows.length === 0 && blocks.length === 0) return '';
  const rowHtml = rows
    .map(([l, v]) => `<tr><th>${escapeHtml(l)}</th><td>${escapeHtml(v ?? '')}</td></tr>`)
    .join('');
  const blockHtml = blocks
    .map(([l, v]) => `<div class="block"><div class="label">${escapeHtml(l)}</div><div class="text">${escapeHtml(v ?? '')}</div></div>`)
    .join('');
  return `<section><h2>${escapeHtml(section.title)}</h2>${rows.length > 0 ? `<table class="rows">${rowHtml}</table>` : ''}${blockHtml}</section>`;
}

/** C7: data fetched for the print besides the referral row (no CPR). */
export type PrintExtras = {
  receiver?: ReferralLetterReceiver | null;
  receiverFailed?: boolean;
  /** Sender, relative and referral diagnosis read from the stored letter XML. */
  letter?: ReferralLetterValues | null;
  /** The letter sent an erstatnings-CPR (REF06-M 3.12). */
  replacementCpr?: boolean;
  /** EnvelopeIdentifier from the stored message (MedCom kuvert-id). */
  envelopeIdentifier?: string | null;
  /** undefined when the attachment status could not be read. */
  receivedAttachments?: ReceivedReferralAttachment[];
};

export type ReferralPrintOptions = PrintExtras & {
  /** Revealed CPR (audited), or null when there is none / the user may not see it. */
  cpr: string | null;
  /** Shown instead of the CPR when cpr is null. */
  cprPlaceholder: string;
  printedAt: Date;
  printedBy?: string | null;
};

export function buildReferralPrintHtml(
  referral: Referral,
  t: PrintTranslate,
  options: ReferralPrintOptions,
): string {
  const name = join([referral.first_name, referral.last_name], ' ') ?? '—';
  const cprText = options.cpr ? formatCprForPrint(options.cpr) : options.cprPlaceholder;
  const pageLabel = t('referrals.print_page');
  const ofLabel = t('referrals.print_page_of');
  const printed = t('referrals.print_printed_at', {
    when: formatDanishDateTime(options.printedAt),
  });
  const body = referralSections(referral, t, options).map(renderSection).join('');
  return `<!doctype html>
<html lang="da"><head><meta charset="utf-8"><title>${escapeHtml(t('referrals.print_title'))} — ${escapeHtml(name)}</title>
<style>
@page {
  size: A4;
  margin: 18mm 15mm 18mm 15mm;
  @bottom-right { content: ${cssString(pageLabel + ' ')} counter(page) ${cssString(' ' + ofLabel + ' ')} counter(pages); font: 9pt Arial, sans-serif; color: #444; }
}
* { box-sizing: border-box; }
body { font: 10pt/1.4 Arial, Helvetica, sans-serif; color: #111; margin: 0; }
table.page { width: 100%; border-collapse: collapse; }
thead .running { border-bottom: 1px solid #999; padding: 0 0 4px; font-size: 9pt; display: flex; justify-content: space-between; }
thead td { padding-bottom: 8px; }
h1 { font-size: 15pt; margin: 4px 0 2px; }
.meta { color: #555; font-size: 9pt; margin-bottom: 8px; }
section { margin-top: 10px; }
h2 { font-size: 11pt; margin: 0 0 4px; padding-bottom: 2px; border-bottom: 1px solid #ccc; break-after: avoid; }
table.rows { width: 100%; border-collapse: collapse; }
table.rows th { text-align: left; font-weight: 600; width: 34%; padding: 2px 8px 2px 0; vertical-align: top; color: #333; }
table.rows td { padding: 2px 0; vertical-align: top; word-break: break-word; }
table.rows tr { break-inside: avoid; }
.block { margin: 4px 0 6px; }
.block .label { font-weight: 600; color: #333; }
.block .text { white-space: pre-wrap; word-break: break-word; }
</style></head>
<body>
<table class="page">
<thead><tr><td><div class="running"><span><strong>${escapeHtml(name)}</strong></span><span>${escapeHtml(options.replacementCpr ? t('referrals.replacement_cpr') : t('referrals.print_cpr'))} <strong>${escapeHtml(cprText)}</strong></span></div></td></tr></thead>
<tbody><tr><td>
<h1>${escapeHtml(t('referrals.print_title'))}</h1>
<div class="meta">${escapeHtml(join([printed, options.printedBy ?? null], ' · ') ?? '')}</div>
${body}
</td></tr></tbody>
</table>
</body></html>`;
}

/**
 * Prints the HTML from a hidden iframe (no pop-up window, so pop-up blockers do
 * not interfere after the async CPR reveal). The iframe is removed afterwards.
 */
export function printHtml(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    iframe.remove();
    throw new Error('print-unavailable');
  }
  doc.open();
  doc.write(html);
  doc.close();
  const cleanup = () => {
    // Drop the document (and the CPR in it) once the dialog is closed.
    setTimeout(() => iframe.remove(), 1000);
  };
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(() => {
    win.focus();
    win.print();
    // Some browsers do not fire afterprint for iframes; print() blocks until closed.
    cleanup();
  }, 50);
}
